// src/components/ImagesTab.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import api from "../services/api";
import ImageModal from "./ImageModal";
import dayjs from "dayjs";
import { Box, FileText, Camera, Trash2, Edit3 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

function coerceArray(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.images)) return payload.images;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.results)) return payload.results;
  return [];
}

function resolvePatientId(props, params, location) {
  const fromSelected = props?.selectedPatient?.id;
  const fromProp =
    props?.patientId ||
    props?.patient_id ||
    props?.patient?.id ||
    props?.patient?.patient_id ||
    props?.patient?.patientId;
  const fromParams = params?.id || params?.patientId || params?.patient_id;
  const search = new URLSearchParams(location?.search || "");
  const fromQuery = search.get("patient_id") || search.get("id");
  const fromStorage = (() => {
    try {
      return window.localStorage.getItem("currentPatientId") || null;
    } catch {
      return null;
    }
  })();

  const pid =
    fromSelected || fromProp || fromParams || fromQuery || fromStorage || null;
  if (pid && pid !== fromStorage) {
    try {
      window.localStorage.setItem("currentPatientId", pid);
    } catch {}
  }
  return pid || null;
}

export default function ImagesTab(props) {
  const { user } = useAuth();
  const permissions = useMemo(() => new Set(user?.permissions || []), [user?.permissions]);
  const params = useParams();
  const location = useLocation();
  const patientId = resolvePatientId(props, params, location);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Modal de upload na aba do prontuário
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  // chave para forçar recarregar lista após upload
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let mounted = true;

    if (!patientId) {
      setLoading(false);
      setErr("Paciente não informado");
      console.warn(
        "[ImagesTab] patientId não encontrado (props/rota/query/localStorage)."
      );
      return;
    }

    async function fetchGet(url, config = {}) {
      try {
        const { data } = await api.get(url, config);
        return { ok: true, data };
      } catch (e) {
        return { ok: false, status: e?.response?.status, error: e };
      }
    }

    // A função enrichWithBase64 não é mais necessária, pois o endpoint /images já retorna o que precisa
    // e o openModal fará a requisição para o dado completo (incluindo STL)

    async function load() {
      setLoading(true);
      setErr(null);
      try {
        let rows = [];

        // 1) rota principal /images?patient_id=
        const r1 = await fetchGet("/images", {
          params: { patient_id: patientId }, // Removido lightweight=false para usar o padrão do backend (que deve retornar miniaturas)
        });
        if (r1.ok) {
          rows = coerceArray(r1.data);
        }

        // 2) fallback /patients/{id}/images (mantido por compatibilidade, mas deve ser descontinuado)
        if (!rows?.length) {
          const r2 = await fetchGet(
            `/patients/${encodeURIComponent(patientId)}/images`
          );
          if (r2.ok) rows = coerceArray(r2.data);
        }

        // 3) fallback /images (todos) e filtra por patient_id
        if (!rows?.length) {
          const r3 = await fetchGet("/images"); // Removido lightweight=false
          if (r3.ok) {
            const all = coerceArray(r3.data);
            rows = all.filter(
              (x) => (x?.patient_id || x?.patientId) === patientId
            );
          }
        }

        if (!mounted) return;

        if (!rows?.length) {
          setItems([]);
          return;
        }

        // Não é mais necessário enriquecer com Base64 aqui
        setItems(rows);
      } catch (e) {
        if (!mounted) return;
        setErr(e?.message || "Falha ao carregar imagens");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    const t = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 10000);
    return () => {
      mounted = false;
      clearTimeout(t);
    };
  }, [patientId, reloadKey]);

  const grid = useMemo(() => {
    return (items || []).map((img) => {
      const mime = img?.mime_type || img?.content_type || img?.mime || "";
      const isPdf =
        mime.includes("pdf") ||
        (img?.original_filename || img?.filename || "")
          .toLowerCase()
          .endsWith(".pdf");
      
      // Adicionado: detecção de STL
      const isStl =
        mime.includes("model/stl") ||
        (img?.original_filename || img?.filename || "")
          .toLowerCase()
          .endsWith(".stl");

      const created =
        img?.created_at || img?.uploaded_at || img?.createdAt || null;
      const filename =
        img?.original_filename ||
        img?.filename ||
        img?.name ||
        `arquivo_${img?.id}`;
      
      // A miniatura (thumbSrc) só é gerada se for imagem e tiver image_data (que não deve vir no lightweight)
      // Para STL e PDF, a miniatura será um ícone.
      const thumbSrc =
        !isPdf && !isStl && mime.startsWith("image/") && img?.image_data
          ? `data:${mime || "image/*"};base64,${img.image_data}`
          : null;

      return {
        id: img?.id,
        patient_id: img?.patient_id || img?.patientId,
        mime,
        isPdf,
        isStl, // Adicionado
        created_at: created,
        filename,
        description: img?.description || "",
        notes: img?.notes || "",
        tags: Array.isArray(img?.tags) ? img.tags : [],
        image_type: img?.image_type || img?.type || "",
        thumbSrc,
        raw: img,
      };
    });
  }, [items]);

  const openModal = async (item) => {
    try {
      // Usa a nova rota /images/{image_id} com include_data=true
      const { data: j } = await api.get(
        `/images/${encodeURIComponent(item.id)}`,
        { params: { include_data: true } }
      );
      
      const imageForModal = {
        ...item.raw,
        ...j, // Sobrescreve com os dados completos, incluindo image_data (Base64)
        original_filename: item.filename,
        mime_type: item.mime || item.raw?.mime_type || "image/*",
        image_type: item.raw?.image_type || "",
        created_at: item.created_at,
      };
      setSelectedImage(imageForModal);
      setIsModalOpen(true);
    } catch (e) {
      console.error("Erro ao carregar dados completos do arquivo:", e);
      // Fallback para abrir a rota raw em nova aba
      const origin = window.location.origin.replace(/\/$/, "");
      window.open(
        `${origin}/api/images/${encodeURIComponent(item.id)}/raw`,
        "_blank"
      );
    }
  };

  const deleteImage = async (imageId) => {
    if (!imageId) return;

    const confirmDelete = window.confirm("Excluir este arquivo do prontuário?");
    if (!confirmDelete) return;

    try {
      const token = window.localStorage.getItem("token") || "";
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL ||
        (typeof window !== "undefined" && window.location.hostname === "localhost"
          ? "http://localhost:8001"
          : "");

      const response = await fetch(
        `${backendUrl}/api/images/${encodeURIComponent(imageId)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

if (!response.ok) {
  let detail = "Erro ao excluir arquivo.";
  try {
    const errorData = await response.json();
    detail = errorData?.detail || detail;
  } catch {}
  throw new Error(detail);
}

      setItems((prev) => prev.filter((img) => img.id !== imageId));
    } catch (error) {
      console.error("Erro ao excluir arquivo:", error);
      window.alert(error?.message || "Erro ao excluir arquivo.");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  if (loading)
    return <div className="p-4 text-gray-600">Carregando imagens…</div>;
  if (err) return <div className="p-4 text-red-600">Erro: {String(err)}</div>;

  const hasImages = grid.length > 0;

  return (
    <div className="p-4">
      {/* Header da aba + botão Nova Imagem */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-800">
          Arquivos e documentos do paciente
        </h3>
        {patientId && permissions.has('images.upload') && (
          <button
            type="button"
            onClick={() => setShowUploadModal(true)}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          >
            + Novo Arquivo
          </button>
        )}
      </div>

      {/* Lista ou mensagem de vazio */}
      {hasImages ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">
          {grid.map((it) => (
            <div
              key={it.id}
              className="group border rounded-md overflow-hidden bg-white hover:shadow"
              title={it.filename}
            >
              <button
                type="button"
                onClick={() => openModal(it)}
                className="w-full text-left focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <Thumb it={it} />
                <div className="px-2 py-2">
                  <div className="text-xs font-medium truncate">
                    {it.filename}
                  </div>
                  {it.created_at && (
                    <div className="text-[11px] text-gray-500">
                      {dayjs(it.created_at).format("DD/MM/YYYY HH:mm")}
                    </div>
                  )}
                </div>
              </button>

              {(permissions.has('images.update') || permissions.has('images.delete')) && <div className="px-2 pb-2 grid grid-cols-2 gap-1">
                {permissions.has('images.update') && <button
                  type="button"
                  onClick={() => setEditingImage(it)}
                  className="inline-flex items-center justify-center gap-1 px-2 py-1 text-xs rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                >
                  <Edit3 className="w-3 h-3" />
                  Editar
                </button>}

                {permissions.has('images.delete') && <button
                  type="button"
                  onClick={() => deleteImage(it.id)}
                  className="inline-flex items-center justify-center gap-1 px-2 py-1 text-xs rounded-md border border-red-200 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-3 h-3" />
                  Excluir
                </button>}
              </div>}
            </div>
          ))}
        </div>
      ) : (
        <div className="p-3 text-gray-500 text-sm border border-dashed border-gray-300 rounded-lg bg-gray-50">
          Nenhum arquivo enviado para este paciente.
        </div>
      )}

      <ImageModal
        image={selectedImage}
        isOpen={isModalOpen}
        onClose={closeModal}
        formatDate={(d) => (d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "")}
      />

      {/* Modal de upload específico do prontuário */}
      {showUploadModal && patientId && (
        <UploadImageModal
          patientId={patientId}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            // fecha modal e força recarregar a lista
            setShowUploadModal(false);
            setReloadKey((v) => v + 1);
          }}
        />
      )}

      {editingImage && (
        <EditImageInfoModal
          image={editingImage}
          onClose={() => setEditingImage(null)}
          onSuccess={() => {
            setEditingImage(null);
            setReloadKey((v) => v + 1);
          }}
        />
      )}      
    </div>
  );
}

function Thumb({ it }) {
  if (it.isStl) {
    return (
      <div className="relative aspect-[4/3] bg-gray-100 flex items-center justify-center text-blue-500">
        <Box className="w-10 h-10" />
        <div className="absolute bottom-0 right-0 bg-blue-500 text-white text-[10px] px-1 py-0.5 rounded-tl-md">STL</div>
      </div>
    );
  }
  if (it.isPdf) {
    return (
      <div className="relative aspect-[4/3] bg-gray-100 flex items-center justify-center text-red-500">
        <FileText className="w-10 h-10" />
        <div className="absolute bottom-0 right-0 bg-red-500 text-white text-[10px] px-1 py-0.5 rounded-tl-md">PDF</div>
      </div>
    );
  }
  if (it.thumbSrc) {
    return (
      <div className="relative aspect-[4/3] bg-gray-100">
        <img
          src={it.thumbSrc}
          alt=""
          className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src =
              "data:image/svg+xml;utf8," +
              encodeURIComponent(
                `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 90'><rect width='120' height='90' fill='#f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-size='10' fill='#9ca3af'>prévia indisponível</text></svg>`
              );
          }}
        />
      </div>
    );
  }
  return (
    <div className="relative aspect-[4/3] bg-gray-100 flex items-center justify-center text-gray-400">
      <Camera className="w-10 h-10" />
    </div>
  );
}

/**
 * Modal de upload de imagem/documento diretamente do prontuário
 */
function UploadImageModal({ patientId, onClose, onSuccess }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentFileName, setCurrentFileName] = useState("");
  const [progress, setProgress] = useState(0);

  const validateFile = (selectedFile) => {
    const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
    let maxSize = 10 * 1024 * 1024;

    if (["jpg", "jpeg", "png", "gif", "webp"].includes(fileExtension)) {
      maxSize = 10 * 1024 * 1024;
    } else if (fileExtension === "pdf") {
      maxSize = 20 * 1024 * 1024;
    } else if (fileExtension === "stl") {
      maxSize = 35 * 1024 * 1024;
    } else {
      return {
        ok: false,
        message: `${selectedFile.name}: tipo de arquivo não permitido.`,
      };
    }

    if (selectedFile.size > maxSize) {
      return {
        ok: false,
        message: `${selectedFile.name}: arquivo muito grande.`,
      };
    }

    return { ok: true };
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (!selectedFiles.length) {
      setFiles([]);
      return;
    }

    const validFiles = [];
    const errors = [];

    selectedFiles.forEach((selectedFile) => {
      const validation = validateFile(selectedFile);
      if (validation.ok) {
        validFiles.push(selectedFile);
      } else {
        errors.push(validation.message);
      }
    });

    if (errors.length) {
      window.alert(errors.join("\n"));
    }

    setFiles(validFiles);
    e.target.value = null;
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!patientId) {
      window.alert("Paciente não identificado para este prontuário.");
      return;
    }

    if (!files.length) {
      window.alert("Selecione um ou mais arquivos para enviar.");
      return;
    }

    setUploading(true);
    setProgress(0);
    setCurrentIndex(0);

    try {
      const token = window.localStorage.getItem("token") || "";

      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];

        setCurrentIndex(index + 1);
        setCurrentFileName(file.name);
        setProgress(0);

        const formData = new FormData();
        formData.append("patient_id", patientId);
        formData.append("file", file);

        await api.post("/images", formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (event) => {
            if (!event.total) return;
            const percent = Math.round((event.loaded * 100) / event.total);
            setProgress(percent);
          },
        });
      }

      window.alert(
        files.length === 1
          ? "Arquivo enviado com sucesso!"
          : `${files.length} arquivos enviados com sucesso!`
      );

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Erro no upload de arquivo do prontuário:", error);
      window.alert(
        error?.response?.data?.detail || "Erro ao enviar arquivo."
      );
    } finally {
      setUploading(false);
      setCurrentFileName("");
      setCurrentIndex(0);
      setProgress(0);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={(e) => {
        if (!uploading && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Enviar arquivos
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Selecione uma ou várias imagens, PDFs ou arquivos STL.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="text-gray-400 hover:text-gray-600 text-sm disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleUpload} className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Arquivo(s) *
            </label>
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              accept="image/*,application/pdf,.stl"
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
              disabled={uploading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Formatos: imagens, PDF e STL. PDF até 20MB. STL até 35MB.
            </p>
          </div>

          {files.length > 0 && !uploading && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs font-medium text-gray-700 mb-2">
                {files.length} arquivo(s) selecionado(s):
              </p>
              <ul className="max-h-32 overflow-y-auto text-xs text-gray-600 space-y-1">
                {files.map((file) => (
                  <li key={`${file.name}-${file.size}`} className="truncate">
                    {file.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {uploading && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-800">
                Enviando arquivos. Não saia desta página até a conclusão.
              </p>
              <p className="text-xs text-amber-700 mt-1 truncate">
                {currentIndex}/{files.length}: {currentFileName}
              </p>

              <div className="mt-3 h-2 w-full rounded-full bg-amber-100 overflow-hidden">
                <div
                  className="h-full bg-amber-500 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <p className="text-[11px] text-amber-700 mt-1">
                {progress}% concluído neste arquivo
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-600 rounded-lg hover:bg-gray-100"
              disabled={uploading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-70"
              disabled={uploading || !files.length}
            >
              {uploading ? "Enviando..." : "Enviar arquivo(s)"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditImageInfoModal({ image, onClose, onSuccess }) {
  const [description, setDescription] = useState(image?.description || "");
  const [notes, setNotes] = useState(image?.notes || "");
  const [tags, setTags] = useState(
    Array.isArray(image?.tags) ? image.tags.join(", ") : ""
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();

    if (!image?.id) {
      window.alert("Arquivo não identificado.");
      return;
    }

    setSaving(true);

    try {
      await api.put(`/images/${encodeURIComponent(image.id)}`, {
        description,
        notes,
        tags: tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
      });

      window.alert("Informações atualizadas com sucesso!");
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Erro ao atualizar informações do arquivo:", error);
      window.alert(
        error?.response?.data?.detail ||
          "Erro ao atualizar informações do arquivo."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={(e) => {
        if (!saving && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Editar informações do arquivo
            </h3>
            <p className="text-xs text-gray-500 mt-1 truncate">
              {image?.filename || image?.original_filename || "Arquivo"}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 text-sm disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSave} className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Radiografia panorâmica inicial"
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observações complementares sobre este arquivo."
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="ex: pré-operatório, panorâmica"
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Separe as tags por vírgula.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-600 rounded-lg hover:bg-gray-100"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-70"
              disabled={saving}
            >
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
